import { NavLink, useLocation } from "react-router-dom";
import { AlertTriangle, CalendarClock, ClipboardList, DoorOpen } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { useTodayVisits } from "@/hooks/security/use-visits";
import { haptic } from "@/components/security/native";
import { cn } from "@/lib/utils";
import { ROUTES } from "@/routes/paths";

/**
 * Os separadores da app do segurança, por ordem de aparição.
 *
 * Exportados porque a casca (`security-app-layout`) os usa também como lista de
 * endereços permitidos — a barra e a tranca têm de dizer a mesma coisa, senão
 * havia um separador que se via mas não abria.
 */
export const SECURITY_TABS: { to: string; label: string; icon: LucideIcon }[] = [
  { to: ROUTES.SECURITY, label: "Hoje", icon: CalendarClock },
  { to: ROUTES.SECURITY_GATE, label: "Portaria", icon: DoorOpen },
  { to: ROUTES.SECURITY_INCIDENT, label: "Ocorrência", icon: AlertTriangle },
  { to: ROUTES.SECURITY_LOG, label: "Turno", icon: ClipboardList },
];

/**
 * Barra de separadores da app do segurança.
 *
 * Vive num ficheiro próprio porque é precisa em DOIS sítios: na casca da PWA
 * (o telemóvel do guarda) e na página dentro do dashboard (um admin a espreitar
 * num telemóvel). Enquanto esteve embutida só na casca, abrir
 * `/dashboard/security` num ecrã estreito dava um ecrã sem navegação nenhuma —
 * via-se um formulário e não havia como sair dele.
 *
 * Desaparece em `lg+`: aí as secções estão todas no ecrã ao mesmo tempo, lado a
 * lado, e não há nada entre que trocar.
 */
export const SecurityTabBar = ({ className }: { className?: string }) => {
  const { pathname } = useLocation();

  // As Visitas não são um separador, mas o posto chega lá pela pesquisa (⌘K) e
  // a página corre dentro da casca. Sem isto a barra ficava sem NENHUM item
  // activo e o guarda não tinha pista de onde estava nem de como voltar.
  // Marcamos "Hoje", que é a vista de onde as visitas vêm.
  const activeTab = pathname === ROUTES.VISITS ? ROUTES.SECURITY : pathname;

  // Badge do separador "Hoje": quantos ainda não chegaram. É a MESMA chave de
  // query que a vista "Hoje" usa — o TanStack Query serve as duas do mesmo
  // cache, portanto isto não acrescenta um pedido à rede.
  const { data: today } = useTodayVisits();
  const porChegar = (today?.items ?? []).filter((v) => v.status === "agendada").length;

  return (
    <nav
      aria-label="Separadores da app"
      className={cn("grid shrink-0 grid-cols-4 border-t border-border/60 bg-background lg:hidden", className)}
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
    >
      {SECURITY_TABS.map((t) => {
        const Icon = t.icon;
        // O activo é calculado aqui, e não pelo `isActive` do NavLink, para as
        // Visitas poderem marcar o separador "Hoje" (ver acima).
        const active = activeTab === t.to;
        return (
          <NavLink
            key={t.to}
            to={t.to}
            // `replace`: os separadores são irmãos, não passos de um caminho.
            // Sem isto, saltar entre eles enchia o histórico e o botão "voltar"
            // do Android percorria-os um a um em vez de fechar a app.
            replace
            onClick={() => haptic()}
            aria-current={active ? "page" : undefined}
            className="relative flex min-h-[56px] flex-col items-center justify-center gap-1 pt-1"
          >
            <span className="relative">
              <Icon
                className={cn(
                  "h-[22px] w-[22px] transition-all",
                  active ? "scale-110 text-primary" : "text-muted-foreground",
                )}
                // Ícone PREENCHIDO quando activo — é assim que o iOS marca a
                // aba corrente; só mudar a cor não chega para ler de relance.
                strokeWidth={active ? 2.4 : 1.8}
              />
              {t.to === ROUTES.SECURITY && porChegar > 0 && (
                <span className="absolute -right-2.5 -top-1.5 flex h-[18px] min-w-[18px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-medium leading-none text-white ring-2 ring-background tabular-nums">
                  {porChegar}
                </span>
              )}
            </span>
            <span
              className={cn(
                "text-[10px] font-medium transition-colors",
                active ? "text-primary" : "text-muted-foreground",
              )}
            >
              {t.label}
            </span>
          </NavLink>
        );
      })}
    </nav>
  );
};
